import React, {useEffect, useState} from 'react'
import {useField} from 'formik'
import {useTranslation} from 'react-i18next'
import {ExternalCarForm} from './ExternalCarForm'
import {InternalDriver} from './InternalDriver'
import {ControlledSelectField} from 'components/Shared/ControlledSelectField'
import {fetchCarsForCarrier} from 'services/CarService'
import {ContractorSelectField} from 'components/Carrier/CarrierSelectField'
import Sections from 'components/Order/sections'
import {FormColumn} from 'components/Shared/CrudComponents'
import {ORDER_CARRIER_READ, ORDER_CARRIER_WRITE, useAuth} from 'context/auth'
import {CarrierFormValues} from './form'
import {fetchCarrierLatestEmails, fetchContractor} from 'services/ContractorService'
import {FieldComponent} from 'components/Shared/FieldComponent'
import {AutosuggestionMailInput} from '../MailField'
import {PrimaryButton} from "../../../../Shared/PrimaryButton";
import {
  ContractorVerificationRequestDialog
} from "../../../../Shared/Dialogs/ContractorVerificationRequestDialog/ContractorVerificationRequestDialog";
import {CustomWarningMessageText, InfoMessageText} from "../../../../Shared/ErrorMessage";
import moment from "moment";
import {useErrorHandling} from "../../../../../context/errorHandling";

export function CarrierForm({
                              fieldName,
                              onCarrierSelect,
                              onFullCarrierData,
                              onContractorCreated,
                              carrierVerificationWarnings,
                              isEditing = false
                            }) {
  const {t} = useTranslation()
  const {hasAnyPrivileges} = useAuth()

  const [, meta, helpers] = useField(fieldName)
  const {value} = meta
  const {setValue} = helpers

  const [enteredCarRegistration, setEnteredCarRegistration] = useState('')

  const [cars, setCars] = useState([])
  const [hasVerificationWarnings, setHasVerificationWarnings] = useState(false)

  // We fill contact info inputs with contact info of carrier selected by user.
  // It's loaded async, thus state is needed to properly update form values.
  // Should be undefined when default carrier is set (order is being copied or edited).
  const [carrierToCopy, setCarrierToCopy] = useState()
  const [verificationRequestDialogOpen, setVerificationRequestDialogOpen] = useState(false)
  const [carrierMailSuggestions, setCarrierMailSuggestions] = useState([])
  const {handleError} = useErrorHandling()

  useEffect(() => {
    if (carrierToCopy) {
      onFullCarrierData(carrierToCopy)
      setCarrierToCopy(null)
      setValue(CarrierFormValues.withAdditionalInfoCopiedFromCarrier(value, carrierToCopy))
    }
  }, [carrierToCopy, onFullCarrierData, value, setValue])

  useEffect(() => {
    fetchCarsForCarrier(value.id)
      .then(setCars)
  }, [value.id])

  useEffect(() => {
    setHasVerificationWarnings(Object.values(carrierVerificationWarnings).some(item => item === true))
  }, [carrierVerificationWarnings, setHasVerificationWarnings])

  useEffect(() => {
    const carrierId = value.id
    if (carrierId) {
      fetchCarrierLatestEmails(carrierId)
        .then(result => setCarrierMailSuggestions(result.map(it => it.email)))
        .catch(handleError)
    }
  }, [value.id, handleError])

  const handleAddExternalCarClick = () => {
    if (enteredCarRegistration) {
      setValue(CarrierFormValues.withExternalCarRegistrationNumberEntered(value, enteredCarRegistration))
    }
  }

  const handleExternalCarRegistrationNumberClearClick = () => {
    setValue(CarrierFormValues.withInternalCarNotSelected(value))
  }

  function handleInternalCarChanged(carId) {
    if (carId) {
      const car = cars.find(c => c.id === carId)
      onCarrierSelect(car.carrierId)
      setValue(CarrierFormValues.withInternalCarSelected(value, car))
      if (car.carrierId !== value.id) {
        fetchContractor(car.carrierId)
          .then(setCarrierToCopy)
      }
    } else {
      onCarrierSelect(value.id)
      setValue(CarrierFormValues.withInternalCarNotSelected(value))
    }
  }

  function handleCarrierChanged(carrierId) {
    if (carrierId) {
      onCarrierSelect(carrierId)
      setValue(CarrierFormValues.withCarrierSelected(value, carrierId))
      fetchContractor(carrierId)
        .then(setCarrierToCopy)
    } else {
      onCarrierSelect(null)
      setValue(CarrierFormValues.noCarrierSelected())
    }
  }

  function warningMessage() {
    if (carrierVerificationWarnings.noHvVerification) {
      return t(`order.warnings.carrier.noHvVerification`)
    } else if (carrierVerificationWarnings.hvExpirationDate) {
      return t(`order.warnings.carrier.hvExpirationDate`)
    } else if (carrierVerificationWarnings.noVerification) {
      return t(`order.warnings.carrier.noVerification`)
    } else if (carrierVerificationWarnings.carrierVerificationExpirationDate) {
      return t(`order.warnings.carrier.carrierVerificationExpirationDate`)
    }
    return ''
  }

  function verificationMessage(value) {

    if (value.hvExpirationDate) {
      return `${t(
        'order.warnings.carrier.hvExpirationDateInfo', {date: `${moment(value.hvExpirationDate).format('DD-MM-YYYY')}`})}`
    }
    if (value.carrierVerificationExpirationDate) {
      return `${t(
        'order.warnings.carrier.carrierVerificationExpirationDateInfo', {date: `${moment(value.carrierVerificationExpirationDate).format('DD-MM-YYYY')}`})}`
    }
    return ''
  }

  return (

    <FormColumn section={Sections.Carrier}
                disabled={!hasAnyPrivileges([ORDER_CARRIER_WRITE])}
                visible={hasAnyPrivileges([ORDER_CARRIER_READ])}>
      <ContractorSelectField
        fieldName={`${fieldName}.id`}
        label={'form.order.carrier.carrierSelect'}
        onChange={handleCarrierChanged}
        onContractorCreated={onContractorCreated}
        role='CARRIER'
      />
      {value && !hasVerificationWarnings && <InfoMessageText message={verificationMessage(value)}/>}
      {hasVerificationWarnings && <CustomWarningMessageText message={warningMessage()}/>}

      {!isEditing && <PrimaryButton
        type="button"
        disabled={!value.id}
        label={t('contractorVerification.requestButton')}
        onClick={() => setVerificationRequestDialogOpen(true)}
        className={'in-form-control-button'}
      />}
      {value.isInternal && (
        <ControlledSelectField
          fieldName={`${fieldName}.internalCar.carId`}
          options={cars}
          getOptionLabel={car => car.registrationNumber}
          label={t('form.order.carrier.carRegistrationNumber')}
          onChange={handleInternalCarChanged}
          noOptionsText={<div className="cursorPointer"
                              onMouseDown={handleAddExternalCarClick}>
            {t('form.select.noOptionsTextWithAdd')}
          </div>}
          onInputChange={setEnteredCarRegistration}
        />
      )}
      {
        value.isInternal && value.internalCar && (
          <InternalDriver driverId={value.internalCar.driverId}/>
        )
      }
      {
        value.isInternal === false && (
          <ExternalCarForm
            fieldName={`${fieldName}.externalCar`}
            onCarRegistrationNumberCleared={handleExternalCarRegistrationNumberClearClick}/>
        )
      }
      <AutosuggestionMailInput
        name={`${fieldName}.contactInfo.emails`}
        suggestions={carrierMailSuggestions || []}
        placeholder={t('form.order.carrier.carrierContactEmails')}/>
      <FieldComponent
        fieldName={`${fieldName}.contactInfo.infoText`}
        label={t('form.order.carrier.carrierContactInfo')}
      />
      {value.id && <ContractorVerificationRequestDialog open={verificationRequestDialogOpen}
                                                        contractorId={value.id}
                                                        onClose={() => {
                                                          setVerificationRequestDialogOpen(false)
                                                        }}
      />}
    </FormColumn>

  )
}
